import * as React from "react";
import { DatePicker as BaseDatePicker } from "react-ui";

import { Tooltip } from "../../Internals/Tooltip/Tooltip";
import { DataPasteContext } from "../../MultiControls/MultiLine/TableDataPasteContext";
import { emptyRef } from "../../TypingUtils";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { ExcelPasteUtils } from "../../MultiControls/MultiLine/ExcelPasteUtils";
import { useScrollTo } from "../../Internals/Contexts/ScrollToContext";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { Text } from "../../ValueViewers/Text/Text";

import * as styles from "./DatePicker.styles";
import { datePickerTestPageObject } from "./DatePickerAdapter";

export interface DataPickerProps extends ControlValidationProps, ControlTooltipProps {
    bindingPath: AbsoluteModelPath;
    value: string | undefined;
    onChange: (value: string | undefined) => void;
    disabled?: boolean;
    isTooltipEnabled?: boolean;
    highlighted?: boolean;
    width?: number | string;
    focusByEnterOnNextControl: (event: React.KeyboardEvent) => void;
    isReadOnly?: boolean;
    onClick?: () => void;
    onBlur?: () => void;
    isLink?: boolean;
    "data-tid"?: string;
    menuPosHorizontal?: "left" | "right";
    menuPosVertical?: "top" | "bottom";
    enableTodayLink?: boolean;
}

export function DatePicker(props: DataPickerProps): JSX.Element {
    const scrollTo = useScrollTo();
    const container = React.useRef<HTMLDivElement>(emptyRef);
    const datePicker = React.useRef<BaseDatePicker>(emptyRef);

    const { error, warning, isTooltipEnabled } = props;
    const tooltipMessage = error || warning;
    const tooltipTrigger = tooltipMessage && isTooltipEnabled ? "hover&focus" : "closed";
    const tooltipPosition = props.tooltipPosition ?? "top left";

    const handleRequestFocus = () => {
        if (container.current != undefined) {
            scrollTo(container.current);
            datePicker.current?.focus();
        }
    };

    useTestPageObject(() => datePickerTestPageObject(props), [props.value]);

    return props.isReadOnly ? (
        <Text
            bindingPath={props.bindingPath}
            data-tid={props["data-tid"]}
            onClick={props.onClick}
            value={props.value}
            isLink={props.isLink}
        />
    ) : (
        <DataPasteContext.Consumer>
            {handlePaste => (
                <FocusManagementObserver onRequestFocus={handleRequestFocus}>
                    <div
                        ref={container}
                        css={styles.root(props.highlighted ?? false)}
                        // onPaste={handlePaste} находится здесь, а не в BaseDatePicker потомучто на момент написания кода в rect-ui не пробрасываются onPaste
                        onPaste={ExcelPasteUtils.WrapHandlePaste(handlePaste, props.bindingPath)}>
                        <Tooltip
                            pos={tooltipPosition}
                            trigger={tooltipTrigger}
                            value={tooltipMessage}
                            width={props.tooltipWidth}>
                            <BaseDatePicker
                                data-tid={props["data-tid"]}
                                ref={datePicker}
                                disabled={props.disabled}
                                width={props.width ?? 120}
                                error={Boolean(error)}
                                value={props.value ?? ""}
                                warning={Boolean(warning)}
                                onKeyDown={props.focusByEnterOnNextControl}
                                onValueChange={props.onChange}
                                onBlur={props.onBlur}
                                menuAlign={props.menuPosHorizontal}
                                menuPos={props.menuPosVertical}
                                enableTodayLink={props.enableTodayLink}
                            />
                        </Tooltip>
                    </div>
                </FocusManagementObserver>
            )}
        </DataPasteContext.Consumer>
    );
}
